import { useEffect, useState } from "react";

export interface ServiceFormValues {
  name: string;
  short_name: string;
  description: string;
  price: string;
  promo_price: string;
  duration: string;
  image: string;
  benefits: string;
  sort_order: string;
}

interface ServiceFormModalProps {
  open: boolean;
  editing: ServiceFormValues | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: ServiceFormValues) => void;
}

const EMPTY: ServiceFormValues = {
  name: "",
  short_name: "",
  description: "",
  price: "",
  promo_price: "",
  duration: "",
  image: "",
  benefits: "",
  sort_order: "0",
};

export default function ServiceFormModal({
  open,
  editing,
  saving,
  onClose,
  onSubmit,
}: ServiceFormModalProps) {
  const [values, setValues] = useState<ServiceFormValues>(EMPTY);
  const [fieldError, setFieldError] = useState("");

  useEffect(() => {
    if (open) {
      setValues(editing ?? EMPTY);
      setFieldError("");
    }
  }, [open, editing]);

  if (!open) return null;

  const set = (key: keyof ServiceFormValues, val: string) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) {
      setFieldError("Vui lòng nhập tên dịch vụ");
      return;
    }
    if (!values.description.trim()) {
      setFieldError("Vui lòng nhập mô tả dịch vụ");
      return;
    }
    if (!values.price || Number(values.price) <= 0) {
      setFieldError("Vui lòng nhập giá hợp lệ");
      return;
    }
    onSubmit(values);
  };

  const inputClass =
    "w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400";
  const labelClass = "block text-xs font-label font-semibold text-foreground-700 mb-1.5";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-background-200/70 sticky top-0 bg-white">
          <h2 className="font-display text-lg font-bold text-foreground-950">
            {editing ? "Sửa dịch vụ" : "Thêm dịch vụ mới"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 md:px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelClass}>Tên dịch vụ *</label>
              <input
                type="text"
                value={values.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="VD: Giảm cân - Giảm mỡ toàn thân"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Tên ngắn</label>
              <input
                type="text"
                value={values.short_name}
                onChange={(e) => set("short_name", e.target.value)}
                placeholder="VD: Giảm cân & Giảm mỡ"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Thời lượng</label>
              <input
                type="text"
                value={values.duration}
                onChange={(e) => set("duration", e.target.value)}
                placeholder="VD: 40 phút"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Giá gốc (VNĐ) *</label>
              <input
                type="number"
                min="0"
                value={values.price}
                onChange={(e) => set("price", e.target.value)}
                placeholder="VD: 599000"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Giá khuyến mãi (VNĐ)</label>
              <input
                type="number"
                min="0"
                value={values.promo_price}
                onChange={(e) => set("promo_price", e.target.value)}
                placeholder="VD: 139000"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Thứ tự hiển thị</label>
              <input
                type="number"
                min="0"
                value={values.sort_order}
                onChange={(e) => set("sort_order", e.target.value)}
                placeholder="VD: 1"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Ảnh dịch vụ (URL)</label>
              <input
                type="text"
                value={values.image}
                onChange={(e) => set("image", e.target.value)}
                placeholder="https://..."
                className={inputClass}
              />
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>Mô tả *</label>
              <textarea
                value={values.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Mô tả chi tiết về dịch vụ..."
                rows={3}
                maxLength={500}
                className={`${inputClass} resize-none`}
              />
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>Lợi ích (mỗi lợi ích một dòng)</label>
              <textarea
                value={values.benefits}
                onChange={(e) => set("benefits", e.target.value)}
                placeholder={"Đốt cháy mỡ thừa hiệu quả\nVóc dáng thon gọn, săn chắc"}
                rows={4}
                className={`${inputClass} resize-none`}
              />
              <p className="text-xs text-foreground-400 font-body mt-1">
                Mỗi dòng sẽ trở thành một lợi ích riêng.
              </p>
            </div>
          </div>

          {fieldError && (
            <div className="mt-4 flex items-center gap-2 text-sm text-accent-700 bg-accent-100 rounded-md px-3 py-2">
              <i className="ri-error-warning-line" />
              <span className="font-body">{fieldError}</span>
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-label font-medium text-foreground-600 hover:text-foreground-900 border border-background-300/60 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-medium rounded-md transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
            >
              {saving && <i className="ri-loader-4-line animate-spin" />}
              {editing ? "Lưu thay đổi" : "Thêm dịch vụ"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}